<template>
  <WdsStack direction-column gap-md>
    <WdsStack justify-space-between align-top>
      <WdsText class="tw-min-w-28" secondary sm>{{ $t('Requested by') }}:</WdsText>
      <WdsStack align-center gap-sm>
        <WdsAvatar
          :initials="textHelpers.nameStringToInitials(requester?.actionedBy.name, 2)"
          :path="requester?.actionedBy.avatar"
          xs
        />
        <WdsText sm>{{ requester?.actionedBy.name }}</WdsText>
        <WdsText secondary sm>{{ date }}</WdsText>
      </WdsStack>
    </WdsStack>
    <WdsStack justify-space-between align-top>
      <WdsText class="tw-min-w-28" secondary sm>
        <WdsStack justify-content-center align-center gap-sm>
          {{ $t('Workflow') }}:
          <WdsTooltip
            :text="$t('Automatically routes approval requests based on specific triggers.')"
          >
            <WdsIcon name="help-filled" :size="16" color="#CDCDD7" />
          </WdsTooltip>
        </WdsStack>
      </WdsText>
      <WdsStack gap-sm>
        <WdsCollapse
          v-if="details.description !== ''"
          v-model:open="showWorkflowDetails"
          :content-id="details.description"
        >
          <template #title>
            <WdsStack align-center justify-end>
              <WdsButtonGhost sm>
                <WdsStack align-center gap-sm>
                  <WdsLabel subtle>{{ triggerReasonText }}</WdsLabel>
                  <WdsIcon
                    name="chevron-down"
                    :size="16"
                    class="tw-transition-all"
                    :class="{ rotate: showWorkflowDetails }"
                  />
                </WdsStack>
              </WdsButtonGhost>
            </WdsStack>
          </template>
          <template #content>
            <WdsText>{{ details.name }}</WdsText>
            <WdsText secondary sm>{{ details.description }}</WdsText>
          </template>
        </WdsCollapse>
        <template v-else>
          {{ details.name }}
        </template>
      </WdsStack>
    </WdsStack>

    <WdsStack justify-space-between align-top>
      <WdsText class="tw-min-w-28" secondary sm>
        <WdsStack justify-content-center align-center gap-sm>
          {{ $t('Step & group') }}:
          <WdsTooltip
            :text="
              $t(
                'Current step & group for this workflow. Approvers from this group handle the request at this stage.',
              )
            "
          >
            <WdsIcon name="help-filled" :size="16" color="#CDCDD7" />
          </WdsTooltip>
        </WdsStack>
      </WdsText>
      <WdsStack align-center gap-sm>
        <WdsCollapse
          v-if="state.approver.userGroup.description !== ''"
          v-model:open="showGroupDetails"
          content-id="user-group-description"
        >
          <template #title>
            <WdsStack align-center justify-end gap-sm>
              <BaseHtml :html="stepHtml" />
              <WdsButtonGhost sm>
                <WdsStack align-center gap-sm>
                  <WdsTag>{{ state.approver.userGroup.name }}</WdsTag>
                  <WdsIcon
                    name="chevron-down"
                    :size="16"
                    class="tw-transition-all"
                    :class="{ rotate: showGroupDetails }"
                  />
                </WdsStack>
              </WdsButtonGhost>
            </WdsStack>
          </template>
          <template #content>
            <WdsText secondary sm>{{ state.approver.userGroup.description }}</WdsText>
          </template>
        </WdsCollapse>
        <WdsTag v-else>{{ state.approver.userGroup.name }}</WdsTag>
      </WdsStack>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsStack,
  WdsText,
  WdsIcon,
  WdsAvatar,
  WdsCollapse,
  WdsButtonGhost,
  WdsTag,
  WdsLabel,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { FragmentType, useFragment } from '@/gql/fragment-masking'
import {
  ApprovalStateFragment,
  WorkflowDetailsFragment,
} from '@/components/Workflows/Approvals/graphql/fragments'
import { ApprovalApprovableState } from '@/gql/graphql'
import useDate from '@/composables/useDate'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { deepClone, textHelpers } from '@/utils/tools'
import BaseHtml from '@/components/BaseHtml.vue'
import { triggerToText } from '@/components/Workflows/Approvals/utilities/triggerToText'

const props = defineProps<{
  approvalStates: FragmentType<typeof ApprovalStateFragment>[]
  workflowDetails: FragmentType<typeof WorkflowDetailsFragment>
  numberOfSteps: number
  currentStep: number
}>()

const states = computed(() =>
  props.approvalStates.map((s) => useFragment(ApprovalStateFragment, deepClone(s))),
)

const stepHtml = `<span>${props.currentStep}</span><span style="color: #777787">/${props.numberOfSteps}</span>`

const requester = computed(() =>
  states.value.find(({ state }) => state === ApprovalApprovableState.Requested),
)

const details = computed(() => useFragment(WorkflowDetailsFragment, props.workflowDetails))
const showWorkflowDetails = ref(false)
const showGroupDetails = ref(false)

const state = states.value[states.value.length - 1]

const { date } = useDate(state.createdAt, 'MMM DD, YYYY')

const triggerReasonText = triggerToText(details.value.trigger)
</script>

<style scoped lang="scss">
@tailwind base;

.request-state {
  @apply tw-rounded;
  @apply tw-border;
  @apply tw-mt-5;
  @apply tw-border-gray-300;
  @apply tw-p-2;

  &.state-approved {
    @apply tw-bg-green-50;
  }

  &.state-rejected,
  &.state-cancelled {
    @apply tw-bg-red-100;
  }
}

.rotate {
  @apply tw-rotate-180;
}
</style>
